import { useCallback } from "react";
import { useSurvey } from "../../common/hooks/api/useSurvey";
import { OrganizationPublicId } from "../../common/service/server/Types";

// Org-scoped UI state for the Access workflow, stored through the generic org-survey store like the Data Flows
// recommendation dismissals (useRecommendationDismissal), so it needs no backend or schema change.
export const ACCESS_WORKFLOW_UI_SURVEY_NAME = "access-workflow-ui-state";
export const DATA_RETRIEVED_VIA_HINT_DISMISSED_SLUG = "data-retrieved-via-hint-dismissed";

/**
 * Whether the org has dismissed the Access request step's "Data retrieved via" hint (DES-43). Orgs that never link
 * vendors would otherwise see it on every Access request. `dismissed` stays undefined until the answers load, so the
 * hint never flashes for an org that already dismissed it.
 */
export function useDataRetrievedViaHintDismissal(orgId: OrganizationPublicId) {
  const { answers, setAnswer, request } = useSurvey(orgId, ACCESS_WORKFLOW_UI_SURVEY_NAME);

  const dismiss = useCallback(() => {
    // Fire-and-forget: the survey store's pending state hides the hint right away. A failed save is already reported
    // by usePatchSurveyAnswer's error snackbar, so only swallow the rejection here.
    setAnswer(DATA_RETRIEVED_VIA_HINT_DISMISSED_SLUG, "true").catch(() => undefined);
  }, [setAnswer]);

  return {
    dismissed: request.success
      ? answers?.[DATA_RETRIEVED_VIA_HINT_DISMISSED_SLUG] === "true"
      : undefined,
    dismiss,
  };
}
